import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogContent
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import examService from '../../../infrastructure/services/examService';
import cameraImportService from '../../../infrastructure/services/cameraImportService';
import apiClient from '../../../infrastructure/services/apiClient';
import { QRCodeSVG } from 'qrcode.react';
import ENV from '../../../config/env';
import comingSoonSvg from '../../../assets/images/SVG/coming soon.svg';

export default function EssayExamPaperSection({
  examId,
  uploadedFiles,
  uploadError = '',
  handleFileUpload,
  handleRemoveFile,
  onSubmitConfirm,
  isExamTimeOver = false,
  uploadTimeLeft = 0,
  formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isAnswerPreviewOpen, setIsAnswerPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [answerPdfUrl, setAnswerPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);

  // Student mobile upload states
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [mobileConnected, setMobileConnected] = useState(false);
  const [mobileImages, setMobileImages] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const socketRef = useRef(null);

  const isComputerUploadDisabled = mobileConnected || mobileImages.length > 0;
  const isMobileUploadDisabled = uploadedFiles.length > 0;

  const getPillStyles = () => {
    if (uploadTimeLeft < 120) { // < 2 minutes (critical)
      return {
        bgcolor: '#fef2f2',
        border: '1.5px solid #fecaca',
        color: '#ef4444',
        '@keyframes pillPulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.05)' },
          '100%': { transform: 'scale(1)' }
        },
        animation: 'pillPulse 1.2s infinite ease-in-out'
      };
    }
    if (uploadTimeLeft < 600) { // < 10 minutes (warning)
      return {
        bgcolor: '#fff7ed',
        border: '1.5px solid #fed7aa',
        color: '#ea580c',
      };
    }
    return { // normal
      bgcolor: '#f0fdf4',
      border: '1.5px solid #bbf7d0',
      color: '#16a34a',
    };
  };

  const pillStyles = getPillStyles();

  // Initialize camera import session
  useEffect(() => {
    let activeSessionId = null;

    const startSession = async () => {
      try {
        const response = await cameraImportService.createStudentSession(examId);
        if (response && response.sessionId) {
          setSessionId(response.sessionId);
          setSessionToken(response.sessionToken);
          activeSessionId = response.sessionId;

          // Connect Socket.io
          socketRef.current = cameraImportService.initiateCameraImportSocket(response.sessionId, response.sessionToken, {
            onMobileConnected: () => {
              setMobileConnected(true);
            },
            onMobileDisconnected: () => {
              setMobileConnected(false);
            },
            onImageUploaded: (data) => {
              setMobileImages((prev) => {
                // Ensure no duplicates
                if (prev.some((img) => img.imageUrl === data.imageUrl)) return prev;
                const sorted = [...prev, data].sort((a, b) => a.sequence - b.sequence);
                return sorted;
              });
            },
            onImageRemoved: (data) => {
              setMobileImages((prev) => prev.filter((img) => img.imageUrl !== data.imageUrl));
            },
            onSessionEnded: () => {
              setMobileConnected(false);
              setMobileImages([]);
            }
          });
        }
      } catch (err) {
        console.error('Failed to create student camera session:', err);
      }
    };

    startSession();

    return () => {
      if (activeSessionId) {
        cameraImportService.endStudentSession(activeSessionId).catch(console.error);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [examId]);

  // Image scanning & processing helper (Commented out as adaptive thresholding compile logic is moved to the backend)
  /*
  const getProcessedImageBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Preserve original image dimensions and aspect ratio to prevent cropping/squishing
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Dynamic adaptive local thresholding (Bradley-Roth algorithm)
        // This resolves shadow artifacts and uneven light conditions typical in camera uploads.
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const width = imgData.width;
        const height = imgData.height;
        const data = imgData.data;

        // Step 1: Convert to Grayscale
        const gray = new Uint8ClampedArray(width * height);
        for (let i = 0; i < data.length; i += 4) {
          gray[i / 4] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        }

        // Step 2: Compute Integral Image (Summed Area Table)
        const integral = new Float64Array(width * height);
        for (let y = 0; y < height; y++) {
          let sum = 0;
          for (let x = 0; x < width; x++) {
            const idx = y * width + x;
            sum += gray[idx];
            integral[idx] = y === 0 ? sum : integral[(y - 1) * width + x] + sum;
          }
        }

        // Step 3: Perform Adaptive Local Thresholding
        const S = Math.floor(Math.max(width, height) / 8); // Window size
        const T = 0.15; // Sensitivity threshold
        const s2 = Math.floor(S / 2);

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = y * width + x;

            const x1 = Math.max(x - s2, 0);
            const x2 = Math.min(x + s2, width - 1);
            const y1 = Math.max(y - s2, 0);
            const y2 = Math.min(y + s2, height - 1);

            const count = (x2 - x1) * (y2 - y1);

            const i_idx1 = y2 * width + x2;
            const i_idx2 = y1 * width + x1;
            const i_idx3 = y1 * width + x2;
            const i_idx4 = y2 * width + x1;

            const sum = integral[i_idx1] - (y1 > 0 ? integral[i_idx3] : 0) - (x1 > 0 ? integral[i_idx4] : 0) + (x1 > 0 && y1 > 0 ? integral[i_idx2] : 0);

            const mean = sum / count;
            const pixelVal = gray[idx];

            // Map pixel to binary high contrast output
            const outputVal = (pixelVal < mean * (1.0 - T)) ? 0 : 255;

            const dIdx = idx * 4;
            data[dIdx] = outputVal;
            data[dIdx + 1] = outputVal;
            data[dIdx + 2] = outputVal;
          }
        }

        ctx.putImageData(imgData, 0, 0);
        resolve({
          base64: canvas.toDataURL('image/jpeg', 0.85),
          width: canvas.width,
          height: canvas.height
        });
      };
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  };
  */

  const handleUseMobileImages = async () => {
    if (mobileImages.length === 0 || !sessionId) return;
    setIsConverting(true);
    try {
      // 1. Trigger backend compilation (Python/OpenCV adaptive thresholding)
      const result = await cameraImportService.compileStudentSession(sessionId);

      if (result && result.isSuccessful && result.pdfUrl) {
        // 2. Fetch the compiled PDF through backend proxy to bypass CORS blocks
        const endpoint = `/student-camera-import/session/download-proxy?url=${encodeURIComponent(result.pdfUrl)}`;
        const pdfBlob = await apiClient.get(endpoint, { responseType: 'blob' });

        // 3. Create file object and feed to standard file upload handler
        const file = new File([pdfBlob], 'mobile-answers.pdf', { type: 'application/pdf' });
        handleFileUpload({ target: { files: [file] } });
      } else {
        console.error('Stitching failed on server:', result?.message);
      }
    } catch (err) {
      console.error('Failed to compile mobile images to PDF via backend:', err);
    } finally {
      setIsConverting(false);
    }
  };

  const handleRemoveMobileImage = async (imageUrl) => {
    if (!sessionId) return;
    try {
      await cameraImportService.removeStudentImage(sessionId, imageUrl);
      setMobileImages((prev) => prev.filter((img) => img.imageUrl !== imageUrl));
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };


  useEffect(() => {
    const uploadedFile = uploadedFiles[0]?.file;
    if (!uploadedFile || uploadedFiles[0]?.status !== 'completed') {
      setAnswerPdfUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(uploadedFile);
    setAnswerPdfUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [uploadedFiles]);

  useEffect(() => {
    let activeUrl = null;
    if (!examId) {
      setPdfError('No exam ID available');
      setPdfLoading(false);
      return;
    }

    const fetchPdf = async () => {
      try {
        setPdfLoading(true);
        setPdfError(null);
        const blob = await examService.generateExamPdf(examId);
        const objectUrl = URL.createObjectURL(blob);
        activeUrl = objectUrl;
        setPdfUrl(objectUrl);
      } catch (err) {
        console.error('Error fetching exam PDF:', err);
        setPdfError(err.message || 'Failed to load question paper preview');
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdf();

    return () => {
      if (activeUrl) {
        URL.revokeObjectURL(activeUrl);
      }
    };
  }, [examId]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
        gap: 4,
        alignItems: 'stretch'
      }}
    >
      {/* Left Column: Question Paper Preview */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1.5px solid #e2e8f0',
          borderRadius: '24px',
          bgcolor: '#ffffff',
          boxShadow: '0 8px 30px -10px rgba(100, 116, 139, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: '800px'
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', mb: 2, fontFamily: 'Poppins' }}>
            Question Paper Preview
          </Typography>

          <Box
            sx={{
              flex: 1,
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1.5px solid #e2e8f0',
              bgcolor: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxHeight: '800px',
              minHeight: '700px'
            }}
          >
            {pdfLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress sx={{ color: '#FF502D' }} />
                <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins' }}>
                  Loading question paper...
                </Typography>
              </Box>
            ) : pdfError ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 4,
                  maxWidth: 360,
                  mx: 'auto'
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '24px',
                    bgcolor: '#fff5f5',
                    border: '1.5px solid #ffe2e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    boxShadow: '0 8px 20px rgba(239, 68, 68, 0.05)'
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="12" x2="12" y2="16" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#1e293b',
                    fontFamily: 'Poppins',
                    mb: 1
                  }}
                >
                  Preview Not Available
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12.5px',
                    color: '#64748b',
                    fontFamily: 'Poppins',
                    lineHeight: 1.5
                  }}
                >
                  {pdfError.toLowerCase().includes('not found') || pdfError.toLowerCase().includes('invalid')
                    ? "The question paper file could not be found or has an invalid file path."
                    : pdfError}
                </Typography>
              </Box>
            ) : isExamTimeOver ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  bgcolor: '#f8fafc',
                  border: '1.5px dashed #cbd5e1',
                  borderRadius: '12px',
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins', mb: 1 }}>
                    Question Paper Locked
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins', lineHeight: 1.5 }}>
                    Exam time has ended. The question paper is locked during the upload grace period.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <iframe
                src={pdfUrl}
                title="Question Paper Preview"
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
              />
            )}

            {isExamTimeOver && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: 'rgba(15, 23, 42, 0.35)',
                  backdropFilter: 'blur(2px)'
                }}
              >
                <Box
                  sx={{
                    maxWidth: 420,
                    width: '100%',
                    textAlign: 'center',
                    p: 3,
                    borderRadius: '20px',
                    bgcolor: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.18)'
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 2,
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#fef2f2',
                      border: '1.5px solid #fecaca'
                    }}
                  >
                    <AccessTimeRoundedIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 750, fontSize: '18px', color: '#1e293b', fontFamily: 'Poppins', mb: 1 }}>
                    Your Writing Time Is Over
                  </Typography>
                  <Typography sx={{ fontSize: '13.5px', color: '#64748b', fontFamily: 'Poppins', lineHeight: 1.55, mb: 2.5 }}>
                    Your writing time has ended. Now you need to upload your answer sheets. You have 30 minutes to upload and click the Submit button.
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: '999px',
                      ...pillStyles
                    }}
                  >
                    <AccessTimeRoundedIcon sx={{ color: pillStyles.color, fontSize: 18 }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: pillStyles.color, fontFamily: 'Poppins' }}>
                      Upload time left: {formatTime(uploadTimeLeft)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Expand icon button */}
            {!pdfLoading && !pdfError && pdfUrl && !isExamTimeOver && (
              <IconButton
                onClick={() => setIsPreviewOpen(true)}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  bgcolor: 'rgba(30, 41, 59, 0.8)',
                  color: '#ffffff',
                  p: 1.25,
                  borderRadius: '12px',
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.2s',
                  zIndex: 10,
                  '&:hover': {
                    bgcolor: 'rgba(30, 41, 59, 0.95)',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              </IconButton>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Right Column: Upload Answer sheet & QR Code */}
      <Stack spacing={2.5} sx={{ height: '100%' }}>
        {/* Upload Area */}
        <Paper
          elevation={0}
          sx={{
            p: 0,
            border: '1.5px solid #e2e8f0',
            borderRadius: '24px',
            bgcolor: '#ffffff',
            boxShadow: '0 12px 40px -16px rgba(15, 23, 42, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: 1
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: '1px solid #f1f5f9',
              background: 'linear-gradient(90deg, #fff7f5 0%, #ffffff 100%)'
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FF8B4A 0%, #FF6244 100%)',
                boxShadow: '0 8px 20px rgba(255, 98, 68, 0.28)'
              }}
            >
              <CloudUploadRoundedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#1e293b', fontFamily: 'Poppins', lineHeight: 1.2 }}>
                Upload Answer Sheet
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#64748b', fontFamily: 'Poppins', mt: 0.25 }}>
                Upload from your computer
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
            {uploadedFiles.length === 0 ? (
              <Box
                onDragOver={(e) => e.preventDefault()}
                onDrop={isComputerUploadDisabled ? null : (e) => { e.preventDefault(); handleFileUpload(e); }}
                onClick={isComputerUploadDisabled ? null : () => document.getElementById('studentFileInput')?.click()}
                sx={{
                  width: '100%',
                  flex: 1,
                  minHeight: '200px',
                  border: '2px dashed',
                  borderColor: isComputerUploadDisabled ? '#cbd5e1' : '#fdba74',
                  borderRadius: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  cursor: isComputerUploadDisabled ? 'not-allowed' : 'pointer',
                  background: isComputerUploadDisabled ? '#f8fafc' : 'linear-gradient(180deg, #fffbf9 0%, #fff7f4 100%)',
                  transition: 'all 0.25s ease',
                  opacity: isComputerUploadDisabled ? 0.6 : 1,
                  '&:hover': isComputerUploadDisabled ? {} : {
                    background: 'linear-gradient(180deg, #fff5f0 0%, #ffede6 100%)',
                    borderColor: '#FF6244',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 28px rgba(255, 98, 68, 0.12)'
                  }
                }}
              >
                <input
                  type="file"
                  id="studentFileInput"
                  hidden
                  disabled={isComputerUploadDisabled}
                  onChange={handleFileUpload}
                  accept=".pdf,application/pdf"
                />
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '20px',
                    bgcolor: '#ffffff',
                    border: '1.5px solid',
                    borderColor: isComputerUploadDisabled ? '#cbd5e1' : '#fed7aa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    boxShadow: isComputerUploadDisabled ? 'none' : '0 8px 24px rgba(255, 98, 68, 0.1)'
                  }}
                >
                  <CloudUploadRoundedIcon sx={{ color: isComputerUploadDisabled ? '#cbd5e1' : '#FF6244', fontSize: 32 }} />
                </Box>
                <Typography textAlign="center" sx={{ fontSize: '15px', fontWeight: 650, color: isComputerUploadDisabled ? '#94a3b8' : '#1e293b', fontFamily: 'Poppins', mb: 0.75 }}>
                  {isComputerUploadDisabled ? 'Computer Upload Disabled' : 'Tap here to upload your answer document'}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: '#64748b', fontFamily: 'Poppins', textAlign: 'center', mb: 2 }}>
                  {isComputerUploadDisabled ? 'Mobile upload flow is active' : 'Upload PDF format only. Single PDF only'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '999px',
                      bgcolor: '#fef2f2',
                      color: '#dc2626',
                      fontSize: '11px',
                      fontWeight: 600,
                      fontFamily: 'Poppins'
                    }}
                  >
                    PDF only
                  </Box>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '999px',
                      bgcolor: '#f0f9ff',
                      color: '#0284c7',
                      fontSize: '11px',
                      fontWeight: 600,
                      fontFamily: 'Poppins'
                    }}
                  >
                    Single file
                  </Box>
                </Stack>
                {uploadError && (
                  <Typography
                    role="alert"
                    sx={{
                      mt: 2,
                      fontSize: '12.5px',
                      color: '#dc2626',
                      fontWeight: 650,
                      fontFamily: 'Poppins',
                      textAlign: 'center'
                    }}
                  >
                    {uploadError}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2.5,
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '20px',
                  background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  position: 'relative',
                  flex: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: '#ffffff',
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(100, 116, 139, 0.1)'
                    }}
                  >
                    <PictureAsPdfRoundedIcon sx={{ color: '#64748b', fontSize: 26 }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1f2937', noWrap: true, textOverflow: 'ellipsis', overflow: 'hidden', fontFamily: 'Poppins' }}>
                      {uploadedFiles[0].name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontFamily: 'Poppins' }}>
                      {uploadedFiles[0].size}
                    </Typography>
                  </Box>

                  {uploadedFiles[0].status === 'uploading' ? (
                    <CircularProgress size={22} sx={{ color: '#64748b' }} />
                  ) : (
                    <CheckCircleRoundedIcon sx={{ color: '#64748b', fontSize: 24, flexShrink: 0 }} />
                  )}

                  {uploadedFiles[0].status !== 'uploading' && (
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                      sx={{
                        color: '#ef4444',
                        bgcolor: '#ffffff',
                        border: '1px solid #fecaca',
                        '&:hover': { bgcolor: '#fef2f2' },
                        p: 1,
                        borderRadius: '12px',
                        flexShrink: 0
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>

                {uploadedFiles[0].status === 'uploading' && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={uploadedFiles[0].progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          background: 'linear-gradient(90deg, #94a3b8 0%, #64748b 100%)'
                        }
                      }}
                    />
                  </Box>
                )}

                {uploadedFiles[0].status === 'completed' && answerPdfUrl && (
                  <Box sx={{ mt: 2.5 }}>
                    <Box
                      onClick={() => setIsAnswerPreviewOpen(true)}
                      sx={{
                        position: 'relative',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        border: '1.5px solid #e2e8f0',
                        bgcolor: '#ffffff',
                        cursor: 'pointer',
                        boxShadow: '0 10px 28px rgba(100, 116, 139, 0.08)',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          borderColor: '#cbd5e1',
                          boxShadow: '0 14px 36px rgba(100, 116, 139, 0.12)',
                          transform: 'translateY(-2px)',
                          '& .answer-preview-overlay': {
                            opacity: 1
                          }
                        }
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #f1f5f9',
                          background: 'linear-gradient(90deg, #f8fafc 0%, #ffffff 100%)'
                        }}
                      >
                        <Typography sx={{ fontSize: '12px', fontWeight: 650, color: '#475569', fontFamily: 'Poppins' }}>
                          Uploaded Answer Preview
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.25,
                            py: 0.4,
                            borderRadius: '999px',
                            bgcolor: '#f1f5f9',
                            color: '#64748b',
                            fontSize: '11px',
                            fontWeight: 600,
                            fontFamily: 'Poppins'
                          }}
                        >
                          <VisibilityRoundedIcon sx={{ fontSize: 14 }} />
                          Tap to view full
                        </Box>
                      </Box>

                      <Box sx={{ position: 'relative', height: 220, bgcolor: '#f8fafc' }}>
                        <iframe
                          src={`${answerPdfUrl}#toolbar=0&navpanes=0`}
                          title="Answer Sheet Preview"
                          width="100%"
                          height="100%"
                          style={{ border: 'none', pointerEvents: 'none' }}
                        />

                        <Box
                          className="answer-preview-overlay"
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.02) 0%, rgba(15, 23, 42, 0.45) 100%)',
                            opacity: 0,
                            transition: 'opacity 0.25s ease'
                          }}
                        >
                          <Button
                            variant="contained"
                            startIcon={<OpenInFullRoundedIcon />}
                            sx={{
                              borderRadius: '999px',
                              textTransform: 'none',
                              fontFamily: 'Poppins',
                              fontWeight: 700,
                              fontSize: '13px',
                              px: 2.5,
                              py: 1,
                              background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                              boxShadow: '0 10px 24px rgba(255, 98, 68, 0.35)',
                              '&:hover': {
                                background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)'
                              }
                            }}
                          >
                            View Full Paper
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                disabled={uploadedFiles.length === 0 || uploadedFiles[0].status !== 'completed'}
                onClick={onSubmitConfirm}
                sx={{
                  borderRadius: '14px',
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: '15px',
                  textTransform: 'none',
                  py: 1.6,
                  background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                  boxShadow: '0 10px 28px rgba(255, 98, 68, 0.32)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
                    boxShadow: '0 14px 32px rgba(255, 98, 68, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  '&.Mui-disabled': {
                    background: '#e8edf3',
                    color: '#94a3b8',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.25s ease'
                }}
              >
                Submit Answer Sheet
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* OR divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', fontFamily: 'Poppins', letterSpacing: '0.08em' }}>
            OR
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
        </Box>

        {/* Mobile Scan QR Area */}
        <Paper
          elevation={0}
          sx={{
            p: 0,
            border: '1.5px solid #e2e8f0',
            borderRadius: '24px',
            bgcolor: '#ffffff',
            boxShadow: '0 12px 40px -16px rgba(15, 23, 42, 0.1)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #f1f5f9',
              background: 'linear-gradient(90deg, #faf5ff 0%, #ffffff 100%)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                  boxShadow: '0 8px 20px rgba(124, 58, 237, 0.28)'
                }}
              >
                <QrCode2RoundedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins', lineHeight: 1.2 }}>
                  Upload via Mobile
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b', fontFamily: 'Poppins', mt: 0.25 }}>
                  Scan QR with your phone camera
                </Typography>
              </Box>
            </Box>

            {/* Connection Status Badge */}
            {sessionId && !isMobileUploadDisabled && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '999px',
                  bgcolor: mobileConnected ? '#edfdfd' : '#f8fafc',
                  color: mobileConnected ? '#0d9488' : '#64748b',
                  border: `1px solid ${mobileConnected ? '#99f6e4' : '#e2e8f0'}`,
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: 'Poppins',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: mobileConnected ? '#0d9488' : '#cbd5e1',
                    animation: mobileConnected ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(0.95)', opacity: 0.5 },
                      '50%': { transform: 'scale(1.2)', opacity: 1 },
                      '100%': { transform: 'scale(0.95)', opacity: 0.5 }
                    }
                  }}
                />
                {mobileConnected ? 'Mobile Connected' : 'Waiting...'}
              </Box>
            )}
          </Box>

          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            {isMobileUploadDisabled ? (
              <Box
                sx={{
                  py: 4,
                  px: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f8fafc',
                  border: '1.5px dashed #cbd5e1',
                  borderRadius: '20px',
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: '#64748b'
                  }}
                >
                  <QrCode2RoundedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#475569', fontFamily: 'Poppins', mb: 0.5 }}>
                  Mobile Upload Disabled
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b', fontFamily: 'Poppins', maxWidth: '260px', lineHeight: 1.5 }}>
                  A file is already uploaded from your computer. Remove it to enable mobile uploads.
                </Typography>
              </Box>
            ) : sessionId && sessionToken ? (
              <>
                {/* Connected State Panel or QR Code */}
                {mobileConnected ? (
                  <Box
                    sx={{
                      py: 4,
                      px: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f0fdf4',
                      border: '1.5px solid #bbf7d0',
                      borderRadius: '20px',
                      mb: 3,
                      width: '100%',
                      animation: 'fadeIn 0.3s ease-out'
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: '#15803d'
                      }}
                    >
                      <CheckCircleRoundedIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#166534', fontFamily: 'Poppins', mb: 0.5 }}>
                      Device Connected
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#15803d', fontFamily: 'Poppins', maxWidth: '260px', lineHeight: 1.5 }}>
                      Your phone is active. Take photos to sync them directly here.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* QR Code */}
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: '20px',
                        bgcolor: '#ffffff',
                        border: '1.5px solid #e2e8f0',
                        boxShadow: 'inset 0 0 0 6px #f8fafc',
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <QRCodeSVG
                        value={`${ENV.PUBLIC_FRONTEND_URL}/exam-upload/${sessionId}?token=${sessionToken}`}
                        size={128}
                        level="M"
                      />
                    </Box>

                    <Typography sx={{ fontSize: '14px', color: '#1e293b', fontWeight: 650, fontFamily: 'Poppins', mb: 0.75 }}>
                      Scan QR to upload
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', fontFamily: 'Poppins', maxWidth: '300px', lineHeight: 1.55, mb: 3 }}>
                      Open your mobile camera, scan the code, and upload your answer sheet directly from your phone.
                    </Typography>
                  </>
                )}

                {/* Mobile Upload Gallery */}
                {mobileImages.length > 0 && (
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', textAlign: 'left', mb: 1, fontFamily: 'Poppins' }}>
                      Mobile Uploads ({mobileImages.length})
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1, width: '100%' }}>
                      {mobileImages.map((img, index) => (
                        <Box
                          key={img.imageUrl}
                          sx={{
                            position: 'relative',
                            width: 80,
                            height: 100,
                            borderRadius: '12px',
                            border: '1.5px solid #cbd5e1',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}
                        >
                          <img
                            src={img.imageUrl}
                            alt={`Page ${img.sequence}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(0, 0, 0, 0.6)',
                              color: '#ffffff',
                              fontSize: '10px',
                              py: 0.25,
                              fontWeight: 600
                            }}
                          >
                            Page {index + 1}
                          </Box>
                          <IconButton
                            onClick={() => handleRemoveMobileImage(img.imageUrl)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              bgcolor: 'rgba(239, 68, 68, 0.9)',
                              color: '#ffffff',
                              p: 0.5,
                              '&:hover': { bgcolor: '#ef4444' }
                            }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleUseMobileImages}
                      disabled={isConverting}
                      startIcon={isConverting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleRoundedIcon />}
                      sx={{
                        mt: 2,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                        boxShadow: '0 8px 20px rgba(124, 58, 237, 0.2)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #b55fe6 0%, #6d28d9 100%)'
                        }
                      }}
                    >
                      {isConverting ? 'Processing Scans...' : 'Use Mobile Uploaded Pages'}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ py: 4 }}>
                <CircularProgress size={30} sx={{ color: '#7c3aed' }} />
                <Typography sx={{ mt: 1.5, fontSize: '13px', color: '#64748b', fontFamily: 'Poppins' }}>
                  Generating session QR code...
                </Typography>
              </Box>
            )}
          </Box>

          {ENV.SHOW_MOBILE_UPLOAD_OVERLAY && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                animation: 'fadeIn 0.4s ease-out',
                gap: 1.5
              }}
            >
              <Box
                component="img"
                src={comingSoonSvg}
                alt="Coming Soon"
                sx={{
                  width: '360px',
                  height: 'auto',
                  maxHeight: '210px',
                  pointerEvents: 'none'
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#FF502D',
                  maxWidth: '320px',
                  lineHeight: 1.5,
                  textAlign: 'center',
                  px: 2
                }}
              >
                Scan QR and Upload feature is under development. Coming soon!
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>

      {/* Full Screen Question Paper Dialog */}
      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(8px)'
          },
          '& .MuiDialog-paper': {
            borderRadius: '24px',
            p: 2,
            maxWidth: '1100px',
            boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
            Question Paper
          </Typography>
          <IconButton
            onClick={() => setIsPreviewOpen(false)}
            sx={{
              color: '#ef4444',
              bgcolor: '#ffffff',
              border: '1.5px solid #fee2e2',
              '&:hover': { bgcolor: '#fee2e2' }
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, height: '80vh', bgcolor: '#f8fafc', borderRadius: '16px', overflow: 'hidden' }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title="Question Paper Preview Full"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Full Screen Answer Sheet Dialog */}
      <Dialog
        open={isAnswerPreviewOpen}
        onClose={() => setIsAnswerPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(8px)'
          },
          '& .MuiDialog-paper': {
            borderRadius: '24px',
            p: 2,
            maxWidth: '1100px',
            boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 0.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
              Uploaded Answer Sheet
            </Typography>
            {uploadedFiles[0] && (
              <Typography sx={{ fontSize: '12px', color: '#64748b', fontFamily: 'Poppins', mt: 0.25 }}>
                {uploadedFiles[0].name} · {uploadedFiles[0].size}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => setIsAnswerPreviewOpen(false)}
            sx={{
              color: '#ef4444',
              bgcolor: '#ffffff',
              border: '1.5px solid #fee2e2',
              '&:hover': { bgcolor: '#fee2e2' }
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, height: '80vh', bgcolor: '#f8fafc', borderRadius: '16px', overflow: 'hidden' }}>
          {answerPdfUrl && (
            <iframe
              src={answerPdfUrl}
              title="Answer Sheet Full Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
